const { startCase, merge } = require("lodash");
const querystring = require("query-string");
const { JOI_LANGUAGE_ERRORS } = require("@dpdgroupuk/mydpd-enums");

exports.arraySeparator = joi => ({
  base: joi.array(),
  name: "array",
  coerce(value, state, options) {
    if (Array.isArray(value) || value instanceof Array) {
      return value;
    }

    if (options.convert && typeof value === "string") {
      return value.split(this._flags.separator, this._flags.limit);
    }

    return value;
  },
  rules: [
    {
      name: "separator",
      description(params) {
        return `Should split the provided string on "${params.separator}"`;
      },
      params: {
        separator: joi
          .string()
          .required()
          .min(1),
      },
      setup(params) {
        this._flags.separator = params.separator;
      },
      validate(params, value, state, options) {
        return value;
      },
    },
  ],
});

exports.queryStringObject = joi => ({
  base: joi.string(),
  name: "string",
  rules: [
    {
      name: "queryStringObject",
      params: {
        options: joi.object(),
      },
      validate(params, value, state, options) {
        const parsed = querystring.parse(value, {
          parseNumbers: false,
          parseBooleans: true,
        });
        const result = joi.validate(parsed, params.options, options);

        if (result && result.error) {
          const firstError = result.error.details[0];
          return this.createOverrideError(
            firstError.type,
            firstError.context,
            {
              path: [...state.path, ...firstError.path],
            },
            options,
            firstError.message
          );
        }
        return parsed;
      },
    },
  ],
});

/**
 * That method overrides default joi messages.
 * That method has to called once on app start
 * @returns {any}
 */
exports.overrideErrors = (params = {}) => {
  params = {
    messages: {},
    joiPath: "joi",
    fieldInKey: false,
    ...params,
  };
  const Joi = require(params.joiPath);
  const errors = JOI_LANGUAGE_ERRORS;

  merge(errors, {
    key: params.fieldInKey ? "Field {{!label}} " : "{{!label}} ",
    date: {
      isoDate: "is invalid",
      base: "is invalid",
      strict: "is invalid",
      min: "is invalid",
      max: "is invalid",
      less: "is invalid",
      greater: "is invalid",
    },
    string: {
      base: "has invalid type",
      min: "has invalid length",
      max: "maximum length exceeded",
      regex: {
        base: "has invalid value",
      },
    },
    number: {
      base: "has invalid type",
      integer: "has invalid type",
      min: "must be greater than or equal to {{limit}}",
      max: "must be less than or equal to {{limit}}",
      negative: "is invalid",
      positive: "is invalid",
    },
    boolean: {
      base: "has invalid type",
    },
    any: {
      required: "is mandatory",
      empty: "is mandatory",
      allowOnly: "has invalid value",
    },
    object: {
      base: "has invalid type",
    },
    array: {
      base: "has invalid type",
    },
    binary: {
      base: "has invalid type",
    },
    ...params.messages,
  });

  return Joi.options({
    language: errors,
  });
};

exports.resolveLabel = ({ context }) => context.label || startCase(context.key);

/**
 * Method to beautify global joi error which is similar to joi.prototype.messages from new joi version
 * @param config - enum of error type to message.
 * Message can be string or function. In order of function, error context is passed to prepare complex message
 * @returns {function(*): *}
 * @example
     Joi.string()
     .min(3)
     .max(15)
     .required()
     .error(
     exports.messages({
          "date.isoDate": error => M.$_IS_INVALID(resolveLabel(error)),
          "string.max": error => M.$_MAXIMUM_LENGTH_EXCEEDED(resolveLabel(error)),
          "string.base": error => M.$_HAS_INVALID_TYPE(resolveLabel(error)),
          "number.base": error => M.$_HAS_INVALID_TYPE(resolveLabel(error)),
        })
     );
 */
exports.messages = config => errors =>
  errors.map(error => {
    if (config.hasOwnProperty(error.type)) {
      error.message =
        typeof config[error.type] === "function"
          ? config[error.type](error)
          : config[error.type];
    }
    return error;
  });

exports.messages.resolveLabel = exports.resolveLabel;

exports.numberMaxLength = joi => ({
  base: joi.number(),
  name: "number",
  language: {
    maxLength: "length must be less than or equal to {{maxLength}}",
  },
  rules: [
    {
      name: "maxLength",
      params: {
        maxLength: joi
          .number()
          .integer()
          .required(),
      },
      validate(params, value, state, options) {
        if (String(value).length > params.maxLength) {
          return this.createError(
            "number.maxLength",
            {
              v: value,
              maxLength: params.maxLength,
            },
            state,
            options
          );
        }

        return value;
      },
    },
  ],
});
